import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEOSchema from '../../components/SEOSchema';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://automat.com.br';

export default function Terms() {
  const { t } = useTranslation();
  
  useEffect(() => {
    document.title = 'Termos de Uso | Automat';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Termos de Uso da Automat. Conheça as regras e condições para utilização da nossa plataforma de automação com IA para WhatsApp.');
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'termos de uso, condições de uso, termos automat, contrato de serviço');
    }

    const metaRobots = document.querySelector('meta[name="robots"]');
    if (metaRobots) {
      metaRobots.setAttribute('content', 'noindex, nofollow');
    }
  }, []);

  const termsSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Termos de Uso",
    "description": "Termos de Uso da Automat. Conheça as regras e condições para utilização da nossa plataforma.",
    "url": `${siteUrl}/termos`
  };

  return (
    <div className="bg-gray-950 min-h-screen">
      <SEOSchema schema={termsSchema} />
      {/* Navigation */}
      <nav className="bg-gray-900/50 backdrop-blur-sm border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <Link to="/" className="flex items-center cursor-pointer">
              <img
                src="/assets/media/automat-logo.png"
                alt="Automat Logo" 
                className="h-10 w-auto"
              />
            </Link>
            <Link 
              to="/" 
              className="text-gray-400 hover:text-purple-400 transition-colors duration-300 cursor-pointer"
            >
              Voltar ao Início
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
          Termos e Condições de Uso e Prestação de Serviços
        </h1>
        <p className="text-gray-400 mb-8">AUTOMAT</p>
        <p className="text-gray-400 mb-12">Última atualização: 26 de novembro de 2025</p>
        
        <div className="space-y-8 text-gray-300">
          <section>
            <p className="mb-6">
              Pelo presente instrumento, a <strong className="text-white">AUTOMAT TECNOLOGIA DA INFORMACAO LTDA</strong> (doravante denominada AUTOMAT), estabelece as CONDIÇÕES GERAIS de uso da plataforma de atendimento inteligente e prestação de serviços de implementação, aceitas pelo CLIENTE (doravante denominado CONTRATANTE) no ato da contratação ou pagamento.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">1. DO OBJETO</h2>
            <p className="mb-4">
              1.1. O presente contrato tem por objeto o licenciamento de uso (SaaS) da plataforma Automat Service (CRM e Chatbot) e a prestação de serviços de consultoria para a configuração (Setup) e treinamento de Agentes de Inteligência Artificial.
            </p>
            <p>
              1.2. A plataforma Automat opera como uma interface integradora, conectando canais de comunicação (WhatsApp) a modelos de Inteligência Artificial (LLMs) e sistemas de gestão.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">2. DA IMPLEMENTAÇÃO (TAXA DE SETUP)</h2>
            <p className="mb-4">
              2.1. A Taxa de Setup (valor de adesão) refere-se exclusivamente aos serviços de consultoria, onboarding, configuração personalizada do sistema, criação e treinamento inicial da IA e reserva de horas da equipe técnica da CONTRATADA.
            </p>
            <p className="mb-4">
              2.2. Natureza do Serviço: Ao agendar a reunião de Kickoff (início) ou preencher o Formulário de Onboarding, a CONTRATANTE reconhece o início imediato da execução dos serviços intelectuais e técnicos personalizados.
            </p>
            <p className="mb-4">
              2.3. Em virtude da natureza personalizada e do consumo imediato de horas técnicas especializadas, a Taxa de Setup é <strong className="text-white">não reembolsável</strong> caso a CONTRATANTE desista do projeto após o início das configurações, realização da reunião de Kickoff ou preenchimento do formulário de onboarding, ressalvadas as garantias legais aplicáveis previstas na Cláusula 5 deste instrumento.
            </p>
            <p>
              2.4. A Taxa de Setup remunera trabalho humano especializado já executado (análise de negócio, configuração de fluxos, treinamento de IA, testes e ajustes), não se confundindo com a licença de software (mensalidade recorrente).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">3. DA INTEGRAÇÃO COM WHATSAPP E TERCEIROS</h2>
            <p className="mb-4">
              3.1. A CONTRATADA utiliza protocolos de integração, APIs oficiais e/ou soluções de terceiros para a conexão com o WhatsApp e Inteligência Artificial. A arquitetura pode variar conforme a necessidade técnica e atualizações das plataformas base (Meta/OpenAI).
            </p>
            <p className="mb-4">
              3.2. Riscos de Bloqueio: A CONTRATANTE declara estar ciente de que o WhatsApp/Meta possui políticas rigorosas de uso. A AUTOMAT não se responsabiliza por eventuais bloqueios, banimentos ou suspensões do número de telefone da CONTRATANTE, decorrentes de denúncias de usuários (spam) ou infração às políticas da Meta.
            </p>
            <p className="mb-4">
              3.3. Boas Práticas Recomendadas:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
              <li>Enviar mensagens apenas para contatos que consentiram previamente;</li>
              <li>Evitar envio em massa indiscriminado;</li>
              <li>Respeitar horários comerciais;</li>
              <li>Oferecer opção clara de descadastramento (opt-out);</li>
              <li>Manter tom profissional e respeitoso nas interações automatizadas.</li>
            </ul>
            <p>
              3.4. A CONTRATANTE é a única responsável pelo conteúdo das mensagens enviadas e pelo cumprimento da LGPD e demais normas aplicáveis.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">4. DA ASSINATURA E PAGAMENTO</h2>
            <p className="mb-4">
              4.1. O serviço é oferecido mediante assinatura recorrente (mensal ou anual), conforme plano escolhido.
            </p>
            <p className="mb-4">
              4.2. A cobrança será realizada automaticamente no cartão de crédito cadastrado, na data de vencimento da fatura.
            </p>
            <p className="mb-4">
              4.3. Em caso de inadimplência, o acesso à plataforma poderá ser suspenso até a regularização do pagamento.
            </p>
            <p>
              4.4. A CONTRATANTE poderá cancelar a assinatura a qualquer momento, sem multa, mas sem direito a reembolso proporcional do período já pago.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">5. DO DIREITO DE ARREPENDIMENTO (7 DIAS)</h2>
            <p className="mb-4">
              5.1. Conforme o Código de Defesa do Consumidor (Art. 49), a CONTRATANTE tem direito de arrependimento no prazo de 7 (sete) dias corridos, contados da contratação ou do primeiro acesso à plataforma.
            </p>
            <p className="mb-4">
              5.2. Para exercer esse direito, basta enviar solicitação formal para: <strong className="text-white">contato@goautomat.com</strong>
            </p>
            <p className="mb-4">
              5.3. Em caso de exercício do direito de arrependimento dentro de 7 (sete) dias, o reembolso incidirá apenas sobre o valor da <strong className="text-white">MENSALIDADE (Licença de Software)</strong>. A Taxa de Setup (Serviço de Implementação) não será reembolsada caso o serviço de onboarding ou configuração já tenha sido iniciado, visto que se trata de hora técnica especializada consumida e trabalho intelectual personalizado já executado.
            </p>
            <p className="mb-4">
              5.4. Caso a CONTRATANTE exerça o direito de arrependimento <strong className="text-white">antes</strong> do início efetivo dos serviços de implementação (ou seja, antes da reunião de Kickoff, preenchimento do formulário de onboarding ou qualquer configuração técnica), o reembolso será integral, incluindo Taxa de Setup e primeira mensalidade.
            </p>
            <p>
              5.5. O reembolso será processado em até 10 (dez) dias úteis, na mesma forma de pagamento utilizada na contratação.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">6. DAS RESPONSABILIDADES DA CONTRATADA</h2>
            <p className="mb-4">
              6.1. Fornecer acesso à plataforma Automat conforme especificações do plano contratado.
            </p>
            <p className="mb-4">
              6.2. Prestar suporte técnico nos canais e horários definidos para cada plano.
            </p>
            <p className="mb-4">
              6.3. Manter a confidencialidade dos dados da CONTRATANTE, conforme Política de Privacidade.
            </p>
            <p>
              6.4. Realizar manutenções preventivas e corretivas, sempre que possível com aviso prévio.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">7. DAS RESPONSABILIDADES DA CONTRATANTE</h2>
            <p className="mb-4">
              7.1. Fornecer informações corretas e completas durante o cadastro e configuração.
            </p>
            <p className="mb-4">
              7.2. Manter a segurança de suas credenciais de acesso (login e senha).
            </p>
            <p className="mb-4">
              7.3. Utilizar a plataforma em conformidade com as leis brasileiras, especialmente LGPD.
            </p>
            <p className="mb-4">
              7.4. Não utilizar o serviço para envio de spam, conteúdo ilegal, ofensivo ou que viole direitos de terceiros.
            </p>
            <p>
              7.5. Realizar backups periódicos de dados críticos, quando aplicável.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">8. DA PROPRIEDADE INTELECTUAL</h2>
            <p className="mb-4">
              8.1. A plataforma Automat, incluindo código-fonte, design, marca e documentação, é de propriedade exclusiva da AUTOMAT.
            </p>
            <p className="mb-4">
              8.2. É vedada a reprodução, distribuição, engenharia reversa ou qualquer uso não autorizado.
            </p>
            <p>
              8.3. Os dados inseridos pela CONTRATANTE permanecem de sua propriedade, sendo a AUTOMAT mera depositária para fins de processamento do serviço.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">9. DA LIMITAÇÃO DE RESPONSABILIDADE</h2>
            <p className="mb-4">
              9.1. A AUTOMAT não se responsabiliza por:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
              <li>Interrupções causadas por terceiros (WhatsApp, Meta, OpenAI, provedores de internet);</li>
              <li>Perda de dados decorrente de ação da CONTRATANTE ou caso fortuito/força maior;</li>
              <li>Danos indiretos, lucros cessantes ou prejuízos comerciais;</li>
              <li>Conteúdo gerado pela IA em situações não previstas ou treinadas;</li>
              <li>Bloqueios ou banimentos aplicados pelo WhatsApp/Meta.</li>
            </ul>
            <p>
              9.2. A responsabilidade total da AUTOMAT está limitada ao valor pago pela CONTRATANTE nos últimos 12 (doze) meses.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">10. DA PROTEÇÃO DE DADOS (LGPD)</h2>
            <p className="mb-4">
              10.1. A AUTOMAT atua como Operadora de Dados, processando informações sob instrução da CONTRATANTE (Controladora).
            </p>
            <p className="mb-4">
              10.2. Todos os dados são armazenados em servidores seguros, com criptografia e controles de acesso.
            </p>
            <p className="mb-4">
              10.3. A CONTRATANTE deve obter consentimento dos titulares de dados antes de coletar e processar informações pessoais.
            </p>
            <p>
              10.4. Para mais detalhes, consulte nossa Política de Privacidade em: <Link to="/privacidade" className="text-purple-400 hover:text-purple-300">goautomat.com/privacidade</Link>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">11. DO CANCELAMENTO E RESCISÃO</h2>
            <p className="mb-4">
              11.1. A CONTRATANTE pode cancelar a assinatura a qualquer momento, através do painel de controle ou solicitação ao suporte.
            </p>
            <p className="mb-4">
              11.2. O cancelamento terá efeito ao final do período já pago, sem reembolso proporcional.
            </p>
            <p className="mb-4">
              11.3. A AUTOMAT pode suspender ou encerrar a conta em caso de:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
              <li>Violação destes Termos de Uso;</li>
              <li>Inadimplência superior a 15 (quinze) dias;</li>
              <li>Uso fraudulento ou ilegal da plataforma;</li>
              <li>Ordem judicial.</li>
            </ul>
            <p>
              11.4. Após o encerramento, os dados poderão ser retidos por até 90 (noventa) dias para fins de backup e auditoria, sendo posteriormente excluídos.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">12. DAS ALTERAÇÕES DOS TERMOS</h2>
            <p className="mb-4">
              12.1. A AUTOMAT reserva-se o direito de modificar estes Termos a qualquer momento.
            </p>
            <p className="mb-4">
              12.2. Alterações significativas serão comunicadas com antecedência mínima de 30 (trinta) dias.
            </p>
            <p>
              12.3. O uso continuado da plataforma após as mudanças implica aceitação tácita dos novos termos.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">13. DO FORO</h2>
            <p>
              13.1. Fica eleito o foro da Comarca de <strong className="text-white">São Paulo/SP</strong> para dirimir quaisquer controvérsias oriundas deste contrato, com renúncia expressa a qualquer outro, por mais privilegiado que seja.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">14. DISPOSIÇÕES FINAIS</h2>
            <p className="mb-4">
              14.1. Este contrato é regido pelas leis brasileiras, especialmente pelo Código de Defesa do Consumidor (Lei 8.078/90), Marco Civil da Internet (Lei 12.965/14) e LGPD (Lei 13.709/18).
            </p>
            <p className="mb-4">
              14.2. A tolerância quanto ao descumprimento de qualquer cláusula não constituirá novação ou renúncia de direitos.
            </p>
            <p>
              14.3. Se qualquer disposição for considerada inválida, as demais permanecerão em pleno vigor.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">15. CONTATO</h2>
            <p className="mb-4">
              Para dúvidas, suporte ou exercício de direitos:
            </p>
            <ul className="list-none space-y-2">
              <li><strong className="text-white">Razão Social:</strong> AUTOMAT TECNOLOGIA DA INFORMACAO LTDA</li>
              <li><strong className="text-white">CNPJ:</strong> 59.640.197/0001-76</li>
              <li><strong className="text-white">E-mail:</strong> contato@goautomat.com</li>
              <li><strong className="text-white">Site:</strong> goautomat.com</li>
            </ul>
          </section>

          <div className="pt-8 border-t border-gray-800 text-sm text-gray-500">
            <p className="mb-2">Última atualização: 26 de novembro de 2025</p>
            <p className="italic">Ao contratar ou utilizar a plataforma Automat, você declara ter lido, compreendido e concordado integralmente com estes Termos e Condições.</p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 border-t border-gray-800 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-gray-500 text-sm">
            <p>© 2024 Automat. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
